/**
 * Controls the FAQ drawer visibility, tab switching and single-open accordion behaviour.
 *
 * Mirrors filter-drawer.js: a body class (`faq-drawer-open`) drives the slide-in.
 *
 * Triggers:
 *   - Any element with class `faq-drawer-toggle`
 *   - Any element with attribute `data-faq-drawer-toggle`
 *   - Any anchor whose href resolves to `#faq` (Shopify menu links can't
 *     carry classes, so the FAQ menu item still opens the drawer instead of
 *     navigating). Modified/middle clicks fall through so the page stays reachable.
 *
 * Dismisses on the close button, overlay click and Escape. Focus is returned to the
 * trigger on close. Only one question is open per category at any time.
 */
const FAQ_BODY_CLASS = 'faq-drawer-open';
const TRIGGER_SELECTOR = '.faq-drawer-toggle, [data-faq-drawer-toggle]';
const FAQ_PAGE_PATH = '#faq';

function getDrawer() {
  return document.querySelector('[data-faq-drawer]');
}

function isFaqPageLink(element) {
  if (!(element instanceof HTMLAnchorElement)) return false;
  const href = element.getAttribute('href');
  if (!href) return false;

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    return url.hash === FAQ_PAGE_PATH;
  } catch {
    return false;
  }
}

/** @type {Element | null} */
let lastTrigger = null;

function openDrawer(trigger) {
  const drawer = getDrawer();
  if (!drawer || document.body.classList.contains(FAQ_BODY_CLASS)) return;

  lastTrigger = trigger instanceof Element ? trigger : null;

  document.body.classList.add(FAQ_BODY_CLASS);
  document.documentElement.setAttribute('scroll-lock', '');
  drawer.setAttribute('aria-hidden', 'false');

  // Focus the dialog itself — not the first tab. Programmatic `.focus()` on a
  // tab paints Safari's focus ring (looks like a border around "Ordering").
  const panel = drawer.querySelector('.faq-drawer__panel');
  if (panel instanceof HTMLElement) panel.focus({ preventScroll: true });
}

function closeDrawer() {
  const drawer = getDrawer();
  if (!drawer || !document.body.classList.contains(FAQ_BODY_CLASS)) return;

  document.body.classList.remove(FAQ_BODY_CLASS);
  document.documentElement.removeAttribute('scroll-lock');
  drawer.setAttribute('aria-hidden', 'true');

  collapseAllQuestions(drawer);

  if (lastTrigger instanceof HTMLElement) {
    lastTrigger.focus();
    lastTrigger = null;
  }
}

function collapseAllQuestions(drawer) {
  for (const item of drawer.querySelectorAll('[data-faq-drawer-item]')) {
    if (item instanceof HTMLDetailsElement) item.open = false;
  }
}

function selectTab(drawer, slug) {
  if (!slug) return;

  for (const tab of drawer.querySelectorAll('[data-faq-drawer-tab]')) {
    const isActive = tab instanceof HTMLElement && tab.dataset.faqDrawerTab === slug;
    tab.classList.toggle('is-active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.setAttribute('tabindex', isActive ? '0' : '-1');
  }

  for (const panel of drawer.querySelectorAll('[data-faq-drawer-panel]')) {
    const isActive = panel instanceof HTMLElement && panel.dataset.faqDrawerPanel === slug;
    panel.classList.toggle('is-active', isActive);
    panel.toggleAttribute('hidden', !isActive);

    if (isActive) {
      panel.scrollTop = 0;
      collapseAllQuestions(panel);
    }
  }

  const panels = drawer.querySelector('.faq-drawer__panels');
  if (panels instanceof HTMLElement) panels.scrollTop = 0;
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const opener = target.closest(TRIGGER_SELECTOR);
  if (opener) {
    event.preventDefault();
    openDrawer(opener);
    return;
  }

  // Let cmd/ctrl/shift/middle-click through so shoppers can still reach the page.
  const isPlainClick = !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0;
  if (isPlainClick) {
    const link = target.closest('a');
    if (link && isFaqPageLink(link)) {
      event.preventDefault();
      openDrawer(link);
      return;
    }
  }

  if (target.closest('[data-faq-drawer-close]') || target.closest('[data-faq-drawer-overlay]')) {
    event.preventDefault();
    closeDrawer();
    return;
  }

  const tab = target.closest('[data-faq-drawer-tab]');
  const drawer = getDrawer();
  if (tab instanceof HTMLElement && drawer && drawer.contains(tab)) {
    event.preventDefault();
    selectTab(drawer, tab.dataset.faqDrawerTab || '');
  }
});

// Single-open accordion per panel: when one <details> opens, close its siblings.
// The `toggle` event doesn't bubble, so wire the listener onto each <details>
// as it enters the DOM.
function wireSingleOpen(root = document) {
  for (const details of root.querySelectorAll('[data-faq-drawer-item]')) {
    if (!(details instanceof HTMLDetailsElement) || details.dataset.faqSingleOpenWired) continue;
    details.dataset.faqSingleOpenWired = '1';
    details.addEventListener('toggle', () => {
      if (!details.open) return;

      const list = details.closest('[data-faq-drawer-list]');
      if (!list) return;

      for (const sibling of list.querySelectorAll('[data-faq-drawer-item]')) {
        if (sibling !== details && sibling instanceof HTMLDetailsElement) sibling.open = false;
      }
    });
  }
}

wireSingleOpen();
document.addEventListener('DOMContentLoaded', () => wireSingleOpen());
// Wire again in case the section is re-rendered by the theme editor.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) wireSingleOpen(event.target);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && document.body.classList.contains(FAQ_BODY_CLASS)) {
    event.preventDefault();
    closeDrawer();
    return;
  }

  if (!document.body.classList.contains(FAQ_BODY_CLASS)) return;

  const drawer = getDrawer();
  if (!drawer) return;

  const tab = event.target instanceof Element ? event.target.closest('[data-faq-drawer-tab]') : null;
  if (!(tab instanceof HTMLElement)) return;

  const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
  if (!keys.includes(event.key)) return;

  const tabs = Array.from(drawer.querySelectorAll('[data-faq-drawer-tab]'));
  let index = tabs.indexOf(tab);
  if (index === -1) return;

  if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
  else if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
  else if (event.key === 'Home') index = 0;
  else if (event.key === 'End') index = tabs.length - 1;

  const next = tabs[index];
  if (!(next instanceof HTMLElement)) return;

  event.preventDefault();
  selectTab(drawer, next.dataset.faqDrawerTab || '');
  next.focus();
});
